import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from 'src/common/utils/api';

// Every query key that renders a document name, across all versions of the
// documents area (uploaded files, the unified list, and the deprecated
// per-type generated document lists). A rename invalidates all of them so the
// new name shows wherever the document appears.
const DOCUMENT_QUERY_SEGMENTS = new Set([
  'data-upload-files',
  'all-documents',
  'suitability-reports',
  'meeting-agendas',
  'meeting-notes',
  'data-exports',
]);

export function useRenameDocument() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending, error, reset } = useMutation<
    unknown,
    Error,
    { fileId: number; displayName: string }
  >({
    mutationFn: ({ fileId, displayName }) => api.patch(`files/${fileId}`, { displayName }),
    onSuccess: () =>
      queryClient.invalidateQueries({
        predicate: query =>
          query.queryKey.some(
            segment => typeof segment === 'string' && DOCUMENT_QUERY_SEGMENTS.has(segment),
          ),
      }),
  });

  const renameDocument = (fileId: number, displayName: string) =>
    mutateAsync({ fileId, displayName });

  return { renameDocument, isRenaming: isPending, error, reset };
}
